import { Lock, CheckCircle2 } from "lucide-react";
import { DatePicker } from "../../DatePicker.js";
import { Field, CollapsibleSection, FieldGrid } from "../sectionShared.js";
import { AsyncButton } from "../../ui/async-button.js";
import { SelectField } from "../../ui/select-field.js";

interface Props {
  isHR: boolean;
  statutoryEditing: boolean; setStatutoryEditing: (v: boolean) => void;
  savingStatutory: boolean;
  /** Reports a save failure. Wired to the global toaster by the page. */
  setStatutoryErr: (v: string) => void;
  statutorySaved: boolean;
  onSave: () => Promise<void>;
  esiNumber: string; setEsiNumber: (v: string) => void;
  uanNumber: string; setUanNumber: (v: string) => void;
  medicalCard: string; setMedicalCard: (v: string) => void;
  pensionStatus: string; setPensionStatus: (v: string) => void;
  appraisalLastDate: string; setAppraisalLastDate: (v: string) => void;
  appraisalDueDate: string; setAppraisalDueDate: (v: string) => void;
  poNumber: string; setPoNumber: (v: string) => void;
}

export function StatutorySection({
  isHR, statutoryEditing, setStatutoryEditing, savingStatutory, setStatutoryErr, statutorySaved, onSave,
  esiNumber, setEsiNumber, uanNumber, setUanNumber, medicalCard, setMedicalCard, pensionStatus, setPensionStatus,
  appraisalLastDate, setAppraisalLastDate, appraisalDueDate, setAppraisalDueDate, poNumber, setPoNumber,
}: Props) {
  return (
    <CollapsibleSection
      id="statutory"
      title={<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>Statutory Information <Lock size={14} /></span>}
      actions={
        <>
          {isHR && !statutoryEditing && (
            <button
              onClick={() => setStatutoryEditing(true)}
              style={{ padding: "5px 14px", fontSize: 12, fontWeight: 600, border: "1.5px solid var(--color-brand-500)", borderRadius: "var(--radius-md)", background: "var(--color-surface-0)", color: "var(--color-brand-700)", cursor: "pointer" }}>
              Edit
            </button>
          )}
          {statutorySaved && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--color-success-fg)", fontWeight: 600 }}><CheckCircle2 size={13} /> Saved</span>
          )}
        </>
      }
    >
      {!statutoryEditing ? (
        <FieldGrid cols="wide" gap="sm">
          {[
            { label: "ESI Number",         value: esiNumber },
            { label: "UAN Number",          value: uanNumber },
            { label: "Medical Card",        value: medicalCard },
            { label: "Pension Status",       value: pensionStatus },
            { label: "Appraisal Last Date", value: appraisalLastDate ? new Date(appraisalLastDate).toLocaleDateString("en-IN") : "" },
            { label: "Appraisal Due Date",  value: appraisalDueDate ? new Date(appraisalDueDate).toLocaleDateString("en-IN") : "" },
            { label: "PO Number",            value: poNumber },
          ].map(({ label, value }) => (
            <div key={label}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-surface-500)", marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 13, color: value ? "var(--color-surface-900)" : "var(--color-surface-300)" }}>{value || "—"}</div>
            </div>
          ))}
        </FieldGrid>
      ) : (
        <div>
          <FieldGrid cols="two" gap="sm">
            <Field label="ESI Number">
              <input style={{ width: "100%", padding: "8px 10px", border: "1.5px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
                value={esiNumber} onChange={e => setEsiNumber(e.target.value)} placeholder="ESI number" />
            </Field>
            <Field label="UAN Number">
              <input style={{ width: "100%", padding: "8px 10px", border: "1.5px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
                value={uanNumber} onChange={e => setUanNumber(e.target.value)} placeholder="Universal Account Number" />
            </Field>
            <Field label="Medical Card Number">
              <input style={{ width: "100%", padding: "8px 10px", border: "1.5px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
                value={medicalCard} onChange={e => setMedicalCard(e.target.value)} placeholder="Medical card number" />
            </Field>
            <Field label="Pension Status">
              <SelectField size="sm"
                value={pensionStatus} onChange={e => setPensionStatus(e.target.value)}>
                <option value="">Select</option>
                <option value="Enrolled">Enrolled</option>
                <option value="Not Enrolled">Not Enrolled</option>
                <option value="Opted Out">Opted Out</option>
              </SelectField>
            </Field>
            <Field label="Appraisal Last Date">
              <DatePicker style={{ width: "100%", padding: "8px 10px", border: "1.5px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
                value={appraisalLastDate} onChange={v => setAppraisalLastDate(v)} />
            </Field>
            <Field label="Appraisal Due Date">
              <DatePicker style={{ width: "100%", padding: "8px 10px", border: "1.5px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
                value={appraisalDueDate} onChange={v => setAppraisalDueDate(v)} />
            </Field>
            <Field label="PO Number">
              <input style={{ width: "100%", padding: "8px 10px", border: "1.5px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
                value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="PO number (contractors)" />
            </Field>
          </FieldGrid>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <AsyncButton
              onClick={() => onSave()}
              confirm={{
                title: "Save statutory details?",
                description: "Writes the statutory and contract fields in this section to the employee's record.",
                actionText: "Save",
              }}
              disabled={savingStatutory}
            >
              Save
            </AsyncButton>
            <button
              onClick={() => { setStatutoryEditing(false); setStatutoryErr(""); }}
              style={{ padding: "8px 18px", background: "var(--color-surface-100)", color: "var(--color-surface-700)", border: "none", borderRadius: "var(--radius-md)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
}
